import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Download,
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Filter,
  Replace,
  Plus,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAPIClient } from '@/hooks/useAPIClient';

interface ImportResult {
  success: boolean;
  mode: string;
  imported_count: number;
  error_count: number;
  errors: Array<{
    index: number;
    error: string;
    question: string;
  }>;
  message: string;
}

interface FewShotExampleData {
  question: string;
  answer: string;
  is_good_example: boolean;
  explanation: string;
  machines: string[];
  submachine_part: string;
  error_code: string;
  tags: string[];
  id?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  usage_count?: number;
}

interface ExportData {
  examples: Array<FewShotExampleData>;
  metadata: {
    exported_at: string;
    total_count: number;
    filters_applied: Record<string, string | boolean>;
  };
}

interface ImportExportControlsProps {
  mode?: 'export-only' | 'import-only' | 'both';
}

export function ImportExportControls({ mode = 'both' }: ImportExportControlsProps) {
  const { toast } = useToast();
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastImportResult, setLastImportResult] = useState<ImportResult | null>(null);

  // Export filters
  const [exportFilters, setExportFilters] = useState({
    machine_filter: '',
    is_good_example: 'all' as 'all' | 'true' | 'false',
    error_code: '',
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      if (exportFilters.machine_filter) params.append('machine_filter', exportFilters.machine_filter);
      if (exportFilters.is_good_example !== 'all') params.append('is_good_example', exportFilters.is_good_example);
      if (exportFilters.error_code) params.append('error_code', exportFilters.error_code);

      const response = await apiClient.get(`/api/few-shot/export?${params.toString()}`);
      return response.data as ExportData;
    },
    onSuccess: (data) => {
      // Create and download file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filterSuffix = Object.values(exportFilters).some(v => v) ? '_filtered' : '';
      link.download = `few_shot_examples_${timestamp}${filterSuffix}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export erfolgreich',
        description: `${data.examples.length} Beispiele`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Export fehlgeschlagen',
        description: error instanceof Error ? error.message : '',
        variant: 'destructive',
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async ({ file, mode }: { file: File; mode: string }) => {
      const text = await file.text();
      const data = JSON.parse(text);

      const response = await apiClient.post(`/api/few-shot/import?mode=${mode}`, data);
      return response.data as ImportResult;
    },
    onSuccess: (result) => {
      setLastImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['few-shot', 'examples'] });
      queryClient.invalidateQueries({ queryKey: ['few-shot', 'stats'] });

      toast({
        title: 'Import erfolgreich',
        description: `${result.imported_count} importiert${result.error_count > 0 ? `, ${result.error_count} Fehler` : ''}`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Import fehlgeschlagen',
        description: error instanceof Error ? error.message : '',
        variant: 'destructive',
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
    } else {
      toast({
        title: 'Ungültige Datei',
        description: 'JSON-Datei erforderlich',
        variant: 'destructive',
      });
    }
  };

  const handleImport = () => {
    if (!importFile) return;

    if (importMode === 'replace') {
      setShowConfirmDialog(true);
    } else {
      importMutation.mutate({ file: importFile, mode: importMode });
    }
  };

  const confirmImport = () => {
    if (importFile) {
      importMutation.mutate({ file: importFile, mode: importMode });
    }
    setShowConfirmDialog(false);
  };

  const resetFilters = () => {
    setExportFilters({
      machine_filter: '',
      is_good_example: 'all',
      error_code: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      {(mode === 'export-only' || mode === 'both') && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Gefilterte Beispiele exportieren
          </CardTitle>
          <CardDescription>
            Laden Sie Few-Shot-Beispiele mit Filteroptionen als JSON-Datei herunter
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="export-machine-part">Maschinentyp (Code)</Label>
              <Input
                id="export-machine-part"
                placeholder="z.B. pmk, mkt"
                value={exportFilters.machine_filter}
                onChange={(e) => setExportFilters(prev => ({ ...prev, machine_filter: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="export-quality">Qualität</Label>
              <Select
                value={exportFilters.is_good_example}
                onValueChange={(value) => setExportFilters(prev => ({ ...prev, is_good_example: value as '' | 'true' | 'false' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="true">Gute Beispiele</SelectItem>
                  <SelectItem value="false">Schlechte Beispiele</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="export-error-code">Fehlercode</Label>
              <Input
                id="export-error-code"
                placeholder="z.B. 1152 Rollenende"
                value={exportFilters.error_code}
                onChange={(e) => setExportFilters(prev => ({ ...prev, error_code: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              className="flex-1"
            >
              {exportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Exportieren
            </Button>
            <Button
              variant="outline"
              onClick={resetFilters}
              disabled={!Object.values(exportFilters).some(v => v)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter zurücksetzen
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Import Section */}
      {(mode === 'import-only' || mode === 'both') && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Beispiele importieren
          </CardTitle>
          <CardDescription>
            Laden Sie Few-Shot-Beispiele aus einer JSON-Datei hoch
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="import-file">JSON-Datei auswählen</Label>
            <Input
              id="import-file"
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="import-mode">Import-Modus</Label>
            <Select
              value={importMode}
              onValueChange={(value) => setImportMode(value as 'merge' | 'replace')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="merge">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    <span>Zusammenführen - Zu bestehenden Beispielen hinzufügen</span>
                  </div>
                </SelectItem>
                <SelectItem value="replace">
                  <div className="flex items-center gap-2">
                    <Replace className="h-4 w-4" />
                    <span>Ersetzen - Alle bestehenden Beispiele löschen</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {importFile && (
            <div className="p-3 bg-muted rounded-md">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="text-sm font-medium">{importFile.name}</span>
                <Badge variant="secondary">{(importFile.size / 1024).toFixed(1)} KB</Badge>
              </div>
            </div>
          )}

          <Button
            onClick={handleImport}
            disabled={!importFile || importMutation.isPending}
            className="w-full"
          >
            {importMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {importMode === 'replace' ? 'Ersetzen' : 'Zusammenführen'}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Import Results */}
      {(mode === 'import-only' || mode === 'both') && lastImportResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {lastImportResult.error_count === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              )}
              Import-Ergebnis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Modus:</span>
                <Badge variant={lastImportResult.mode === 'replace' ? 'destructive' : 'default'}>
                  {lastImportResult.mode === 'replace' ? 'Ersetzt' : 'Zusammengeführt'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>Erfolgreich importiert:</span>
                <Badge variant="default">{lastImportResult.imported_count}</Badge>
              </div>
              {lastImportResult.error_count > 0 && (
                <div className="flex justify-between">
                  <span>Fehler:</span>
                  <Badge variant="destructive">{lastImportResult.error_count}</Badge>
                </div>
              )}
            </div>

            {lastImportResult.errors.length > 0 && (
              <div className="mt-4">
                <Label>Fehlerdetails:</Label>
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {lastImportResult.errors.map((error, index) => (
                    <div key={index} className="text-xs p-2 bg-destructive/10 rounded border">
                      <div className="font-medium">Zeile {error.index + 1}</div>
                      <div className="text-muted-foreground">{error.error}</div>
                      <div className="text-muted-foreground truncate">{error.question}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Alle Beispiele ersetzen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sie sind dabei, alle bestehenden Few-Shot-Beispiele zu löschen und durch die Inhalte
              der ausgewählten Datei zu ersetzen. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImport} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Ja, alle ersetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}